import { Grid, Typography } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import React, { useEffect, useRef, useState } from 'react'
import { onEditAction } from '../../core/audit'
import {
  auditColumn,
  CollectionTable,
  Column,
  deleteColumn,
  editColumn,
  FilterControl,
  shimColumn,
} from '../../core/collections'
import { Auto, Text } from '../../core/controls'
import { DownCodeApi } from '../../core/downcode-api'
import { Expansion, ExpansionDetails, ExpansionSummary } from '../../core/expansion'
import { personName, personNameWithDegree, truncateSpan } from '../../core/format'
import { orange } from '../../core/theme'
import { UndoRedo } from '../../core/undo-redo'
import { isAuditReviewWorkflow, isReviewWorkflow } from '../../core/workflow-api'
import { isResolved } from '../../core/worksheets'

export function DownCodeForm(api: DownCodeApi) {
  const form = useRef(api.form).current
  const { targetCPTCode, actualCPTCode, reason, provider, comment } = form.controls

  return (
    <Grid
      container
      item
      xs={12}
      spacing={2}
      style={{
        width: '100%',
        paddingLeft: '12px',
        paddingTop: '6px',
        paddingBottom: '8px',
      }}
    >
      <Grid item xs={2}>
        <Auto {...targetCPTCode} />
      </Grid>
      <Grid item xs={2}>
        <Auto {...actualCPTCode} />
      </Grid>
      <Grid item xs={3}>
        <Auto {...reason} />
      </Grid>
      <Grid item xs={3}>
        <Auto {...provider} />
      </Grid>
      <Grid item xs={2}>
        <Text {...comment} />
      </Grid>
    </Grid>
  )
}

export function DownCodeCollection(api: DownCodeApi) {
  const { list, context } = api
  const { auditDownCode } = context.config

  const isReview = isAuditReviewWorkflow()
  const actions = isReview
    ? [auditColumn(onEditAction('Edit Downcode Audit Comments', () => list.actions.refresh(), false, auditDownCode))]
    : []

  const columns: Column[] = [
    {
      name: 'targetCPTCode',
      label: 'Target',
      style: { width: '80px' },
      component: (e) => truncateSpan(e.value.targetCPTCode, '80px'),
    },
    {
      name: 'actualCPTCode',
      label: 'Actual',
      style: { width: '80px' },
      component: (e) => truncateSpan(e.value.actualCPTCode, '80px'),
    },
    {
      name: 'reason',
      label: 'Reason',
      component: (e) => truncateSpan(e.value.reason, '80px'),
    },
    {
      name: 'physician',
      label: 'Physician',
      component: (e) => truncateSpan(personNameWithDegree(e.value.provider), '80px'),
    },
    {
      name: 'comment',
      label: 'Comments',
      component: (e) => truncateSpan(e.value.comment, '80px'),
    },
    ...actions,
    editColumn(
      (e) => api.editItem(e.value),
      (v) => !isResolved(v),
    ),
    deleteColumn(
      (e) => api.removeItem(e.value),
      (v) => !isResolved(v),
    ),
    shimColumn(),
  ]

  const values = list.data((s) => s.values)
  const [expanded, setExpanded] = useState(values.length > 0)
  const [clicked, setClicked] = useState(false)

  useEffect(() => {
    if (!clicked && !expanded) {
      setExpanded(values.length > 0)
    }
  })

  const onClicked = () => {
    setExpanded(!expanded)
    setClicked(true)
  }

  return (
    <Expansion expanded={expanded} id="downcode_expansion">
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon id="downcode_expend_more" />}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: 24,
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}
        >
          <Typography variant="subtitle2">{`DOWNCODES ${values.length ? '- ' + values.length : ''}`}</Typography>
          <div style={{ display: 'flex', flex: 1, flexGrow: 1 }} />

          <div onClick={(e) => e.stopPropagation()} id="downcode_undoredo">
            <UndoRedo api={api.listChanges} htmlColor={orange} label="downcode" />
          </div>

          <FilterControl api={list} />
        </div>
      </ExpansionSummary>
      <ExpansionDetails>
        <CollectionTable columns={columns} api={list} containerProps={{ elevation: 0 }} name="downcode_table" label={`DOWNCODES ${values.length ? '- ' + values.length : ''}`} />
      </ExpansionDetails>
    </Expansion>
  )
}
